import PropTypes from 'prop-types';
import { useState } from 'react';
// @mui
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import Collapse from '@mui/material/Collapse';
// assets
import { PlanFreeIcon, PlanStarterIcon, PlanPremiumIcon } from '../../assets/icons';
// components
import Label from '../../components/label';
import Iconify from '../../components/iconify';

// ----------------------------------------------------------------------

export default function PricingCard({ card, sx, billingPeriod = '6month', ...other }) {
  const { subscription, prices, caption, lists, labelAction, additionalUsers } = card;
  const [expanded, setExpanded] = useState(false);

  const basic = subscription === 'basic';

  const agentic = subscription === 'agentic';

  const intelligent = subscription === 'intelligent';

  const renderIcon = (
    <Stack direction="row" alignItems="center" justifyContent="space-between">
      <Box sx={{ width: 48, height: 48 }}>
        {basic && <PlanFreeIcon />}
        {agentic && <PlanStarterIcon />}
        {intelligent && <PlanPremiumIcon />}
      </Box>

      {agentic && <Label color="info">POPULAR</Label>}
    </Stack>
  );

  const renderSubscription = (
    <Stack spacing={1}>
      <Typography variant="h4" sx={{ textTransform: 'capitalize' }}>
        {subscription}
      </Typography>
      <Typography variant="subtitle2">{caption}</Typography>
    </Stack>
  );

  const monthlyPrice = prices[billingPeriod];

  const renderPrice = (
    <Stack spacing={2.5}>
      <Stack direction="row">
        <Typography variant="h4">$</Typography>

        <Typography variant="h2">{monthlyPrice}</Typography>

        <Typography
          component="span"
          sx={{
            alignSelf: 'center',
            color: 'text.disabled',
            ml: 1,
            typography: 'body2',
          }}
        >
          {'/ mo'}
        </Typography>
      </Stack>

      {additionalUsers && (
        <Stack>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            onClick={() => setExpanded(!expanded)}
            sx={{
              p: 1.5,
              borderRadius: 1.5,
              bgcolor: (theme) => alpha(theme.palette.grey[500], 0.08),
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              '&:hover': {
                bgcolor: (theme) => alpha(theme.palette.grey[500], 0.12),
              },
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <Iconify
                icon="eva:plus-circle-outline"
                width={16}
                sx={{ color: 'text.secondary' }}
              />
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                Additional Users
              </Typography>
            </Stack>
            <Iconify
              icon={expanded ? 'eva:chevron-up-fill' : 'eva:chevron-down-fill'}
              width={20}
              sx={{
                color: 'text.secondary',
                transition: 'transform 0.3s ease',
              }}
            />
          </Stack>

          <Collapse in={expanded}>
            <Stack
              spacing={0.5}
              sx={{
                pt: 1.5,
                pl: 2,
              }}
            >
              <Typography variant="body2" sx={{ color: 'text.primary' }}>
                ${additionalUsers[billingPeriod].user}/mo per additional user
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.primary' }}>
                ${additionalUsers[billingPeriod].admin}/mo per additional admin
              </Typography>
            </Stack>
          </Collapse>
        </Stack>
      )}
    </Stack>
  );

  const renderList = (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Box component="span" sx={{ typography: 'overline' }}>
          Features
        </Box>
        <Link variant="body2" color="inherit" underline="always">
          All
        </Link>
      </Stack>

      {lists.map((item) => (
        <Stack
          key={item}
          spacing={1}
          direction="row"
          alignItems="center"
          sx={{
            typography: 'body2',
          }}
        >
          <Iconify icon="eva:checkmark-fill" width={16} sx={{ mr: 1 }} />
          {item}
        </Stack>
      ))}
    </Stack>
  );

  return (
    <Stack
      spacing={5}
      sx={{
        p: 5,
        borderRadius: 2,
        boxShadow: (theme) => ({
          xs: theme.customShadows.card,
          md: 'none',
        }),
        ...(agentic && {
          borderTopRightRadius: { md: 0 },
          borderBottomRightRadius: { md: 0 },
        }),
        ...((agentic || intelligent) && {
          boxShadow: (theme) => ({
            xs: theme.customShadows.card,
            md: `-40px 40px 80px 0px ${alpha(
              theme.palette.mode === 'light' ? theme.palette.grey[500] : theme.palette.common.black,
              0.16
            )}`,
          }),
        }),
        ...sx,
      }}
      {...other}
    >
      {renderIcon}

      {renderSubscription}

      {renderPrice}

      <Divider sx={{ borderStyle: 'dashed' }} />

      {renderList}

      <Button
        fullWidth
        size="large"
        variant="contained"
        disabled={intelligent}
        color={(basic || agentic) ? 'primary' : 'inherit'}
      >
        {`Choose ${subscription.charAt(0).toUpperCase()}${subscription.slice(1)}`}
      </Button>
    </Stack>
  );
}

PricingCard.propTypes = {
  card: PropTypes.object,
  sx: PropTypes.object,
  billingPeriod: PropTypes.string,
};
